import { FC } from 'react';
import { usePageMessages } from 'components/new/Page/usePageMessages';
import { match } from 'ts-pattern';
import FullHeightView from 'components/new/FullHeightView';
import VerticalTabs from 'components/new/VerticalTabs';
import { useVerticalTabs } from 'components/new/VerticalTabs/useVerticalTabs';
import { Column, Row } from 'further-ui/components/Layout';
import { Button, Typography } from '@mui/material';
import { getSectionDotColor } from '../dotColors';
import { styled } from '@mui/material/styles';
import { TagName } from 'further-types/tag';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { UseTrancheFormReturn } from '../../useTrancheForm';
import { TrancheData, TrancheFormSection } from '../../types';
import useHandleTrancheSubmit from '../useHandleTrancheSubmit';
import GeneralInformation from './GeneralInformation';
import DataSnapshot from './DataSnapshot';
import SubscriptionDocumentation from './SubscriptionDocumentation';
import FeeManagement from './FeeManagement';
import BankDetails from './BankDetails';
import DailySummaryEmail from './DailySummaryEmail';
import EmailCustomisation from './EmailCustomisation';
import FinalDeal from './FinalDeal';
import api, { ApiInputs } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { SUBSCRIPTION_FUNNEL } from 'adminConstants/routes';
import { getQueryKey } from '@trpc/react-query';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import useListPendingAdvisedForFund from 'hooks/data/investment/useListPendingAdvisedForFund';
import useReassignOrDeletePendingAdvised from 'hooks/data/investment/useReassignOrDeletePendingAdvised';
import { useFunds } from 'hooks/data/fund/useFunds';
import TrancheSelector from 'components/TrancheSelector';
import useIsSyndicate from '../../useIsSyndicate';
import TypographyButton from 'further-ui/components/TypographyButton';

const TabContent = styled(Column)(({ theme }) => ({
  paddingTop: theme.spacing(theme.spacingSizes.lg),
  paddingBottom: theme.spacing(theme.spacingSizes.lg),
}));

type Props = {
  form: UseTrancheFormReturn;
  tranche?: TrancheData;
  isPublishable: boolean | null;
  isCreating: boolean;
  hasInvestments: boolean;
  isStatusUpdating: boolean;
};

const TrancheEdit: FC<Props> = ({
  form,
  tranche,
  isPublishable,
  isCreating,
  hasInvestments,
  isStatusUpdating,
}) => {
  const { addMessage, removeMessage } = usePageMessages();
  const { selectedTab, setSelectedTab } = useVerticalTabs<TrancheFormSection>(
    TrancheFormSection.General,
  );
  const { firmId: usersFirmId } = useGetRole();
  const tags = api.eisTag.list.useQuery({});
  const id = tranche?.id ?? '';

  const create = api.fund.create.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Tranche created successfully.',
      invalidations: [['tranches']],
    }),
  );
  const update = api.fund.update.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Tranche updated successfully.',
      invalidations: [['tranches'], getQueryKey(api.fund.get)],
    }),
  );
  const updateFeeManagement = useDisclosure<{
    payload: ApiInputs['fund']['update'];
  }>();
  const closeTrancheDisclosure = useDisclosure<{
    payload: ApiInputs['fund']['update'];
    targetFundId?: string | null;
  }>();
  const pendingAdvised = useListPendingAdvisedForFund(id);
  const reassignOrDeleteMutation = useReassignOrDeletePendingAdvised();

  const [canEdit] = useHasPermissions([RolePermission.EditTranche]);

  const watchedTag = form.watch('tag');
  const selectedTag = tags.data?.find((t) => t.id === watchedTag);
  const isSyndicate = useIsSyndicate(watchedTag);
  const isLP = selectedTag?.name === TagName.LP;
  const selectedFirm = form.watch('firmId');
  const { tranches } = useFunds({ firmId: selectedFirm || usersFirmId });

  const showDraftDots = isPublishable === false;
  const isSaving = create.isPending || update.isPending;

  const { errors } = form.formState;

  const handleSave = useHandleTrancheSubmit({
    form,
    tranche,
    id,
    isSyndicate,
    canEdit,
    create,
    update,
    pendingAdvised,
    closeTrancheDisclosure,
    updateFeeManagement,
    addMessage,
    removeMessage,
  });

  const handleConfirmFeeManagement = () => {
    update.mutate(updateFeeManagement.actionProps.payload);
    updateFeeManagement.onClose();
  };

  const handleConfirmCloseTranche = async () => {
    if (!id) return;
    await reassignOrDeleteMutation.mutateAsync({
      sourceFundId: id,
      targetFundId: closeTrancheDisclosure.actionProps.targetFundId || null,
    });
    update.mutate(closeTrancheDisclosure.actionProps.payload);
    closeTrancheDisclosure.onClose();
  };

  return (
    <FullHeightView
      innerPaddingBottom="lg"
      actionBar={
        canEdit && (!id || form.formState.isDirty) ? (
          <Row justify="flex-end">
            <Button
              size="large"
              variant="contained"
              disabled={isSaving || isStatusUpdating}
              loading={isSaving}
              onClick={handleSave}
            >
              Save
            </Button>
          </Row>
        ) : undefined
      }
    >
      <VerticalTabs
        tabs={[
          {
            label: 'General',
            id: TrancheFormSection.General,
            dotColor: getSectionDotColor(
              TrancheFormSection.General,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
          {
            label: 'Data snapshot',
            id: TrancheFormSection.DataSnapshot,
            dotColor: getSectionDotColor(
              TrancheFormSection.DataSnapshot,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
          {
            label: 'Subscription documentation',
            id: TrancheFormSection.SubscriptionDocumentation,
            dotColor: getSectionDotColor(
              TrancheFormSection.SubscriptionDocumentation,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
          {
            label: 'Fee management',
            id: TrancheFormSection.FeeManagement,
            dotColor: getSectionDotColor(
              TrancheFormSection.FeeManagement,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
          {
            label: 'Bank details',
            id: TrancheFormSection.BankDetails,
            dotColor: getSectionDotColor(
              TrancheFormSection.BankDetails,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
          {
            label: 'Daily summary email',
            id: TrancheFormSection.DailySummaryEmail,
            dotColor: getSectionDotColor(
              TrancheFormSection.DailySummaryEmail,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
          {
            label: 'Email customisation',
            id: TrancheFormSection.EmailCustomisation,
            dotColor: getSectionDotColor(
              TrancheFormSection.EmailCustomisation,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
          {
            label: 'Final deal',
            id: TrancheFormSection.FinalDeal,
            dotColor: getSectionDotColor(
              TrancheFormSection.FinalDeal,
              errors,
              isPublishable,
              isSyndicate,
            ),
          },
        ]}
        fullHeight
        responsive
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        renderContent={(tab) => (
          <TabContent fullWidth>
            {match(tab)
              .with(TrancheFormSection.General, () => (
                <GeneralInformation
                  control={form.control}
                  tagList={tags.data ?? []}
                  currency={CurrencyCode.GBP}
                  isCreating={isCreating}
                  hasInvestments={hasInvestments}
                  showDraftDots={showDraftDots}
                />
              ))
              .with(TrancheFormSection.DataSnapshot, () => (
                <DataSnapshot
                  control={form.control}
                  currency={CurrencyCode.GBP}
                />
              ))
              .with(TrancheFormSection.SubscriptionDocumentation, () => (
                <SubscriptionDocumentation
                  control={form.control}
                  isSyndicate={isSyndicate}
                  isLP={isLP}
                  showDraftDots={showDraftDots}
                />
              ))
              .with(TrancheFormSection.FeeManagement, () => (
                <FeeManagement
                  control={form.control}
                  firmId={selectedFirm}
                  currency={CurrencyCode.GBP}
                />
              ))
              .with(TrancheFormSection.BankDetails, () => (
                <BankDetails
                  control={form.control}
                  firmId={selectedFirm}
                  showDraftDots={showDraftDots}
                />
              ))
              .with(TrancheFormSection.DailySummaryEmail, () => (
                <DailySummaryEmail control={form.control} />
              ))
              .with(TrancheFormSection.EmailCustomisation, () => (
                <EmailCustomisation control={form.control} />
              ))
              .with(TrancheFormSection.FinalDeal, () =>
                id ? (
                  <FinalDeal
                    fundId={id}
                    hasKiCertificate={tranche?.hasKiCertificate ?? false}
                  />
                ) : null,
              )
              .exhaustive()}
          </TabContent>
        )}
      />
      <ConfirmDialog
        open={updateFeeManagement.isOpen}
        onClose={updateFeeManagement.onClose}
        maxWidth="sm"
        title="Warning"
        content={
          <Typography variant="body2">
            <b>Please note:</b> Changes to default fee settings will only apply
            to future subscriptions. To update existing subscriptions in this
            tranche, please navigate to individual subscription pages and edit
            the selected fee preset.
          </Typography>
        }
        actions={{
          cancel: { label: 'Cancel' },
          confirm: { label: 'Update', onClick: handleConfirmFeeManagement },
        }}
      />
      <ConfirmDialog
        open={closeTrancheDisclosure.isOpen}
        onClose={closeTrancheDisclosure.onClose}
        title="Live advised applications"
        maxWidth="sm"
        loading={reassignOrDeleteMutation.isPending || update.isPending}
        content={
          <Column spacing="lg">
            <Typography variant="body2">
              There are pending advised applications for this tranche which you
              can view on the{' '}
              <TypographyButton
                text="Subscription Funnel"
                href={SUBSCRIPTION_FUNNEL}
                target="_blank"
                rel="noreferrer"
              />{' '}
              page. Select a new tranche for these applications below. If no
              tranche is selected, the system will delete pending applications.
            </Typography>
            <TrancheSelector
              firmId={selectedFirm}
              size="small"
              openOnly
              selectedTranche={
                closeTrancheDisclosure.actionProps.targetFundId && tranches.data
                  ? tranches.data.find(
                      ({ _id }) =>
                        _id === closeTrancheDisclosure.actionProps.targetFundId,
                    ) ?? null
                  : null
              }
              onChange={(selected) => {
                closeTrancheDisclosure.stageAction({
                  ...closeTrancheDisclosure.actionProps,
                  targetFundId: selected?._id ?? null,
                });
              }}
            />
          </Column>
        }
        actions={{
          cancel: { label: 'Cancel' },
          confirm: {
            label: 'Close tranche',
            variant: 'contained',
            onClick: handleConfirmCloseTranche,
            disabled:
              reassignOrDeleteMutation.isPending ||
              update.isPending ||
              !pendingAdvised.data,
          },
        }}
      />
    </FullHeightView>
  );
};

export default TrancheEdit;
